import { IImage } from "@faire/web-api/indigofair/data/IImage";
import {
  IOptimizeImageOptions,
  optimizeImageUrl,
  optimizeProductImageUrl,
} from "@faire/web/common/images/optimizeImages";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web/common/media";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import {
  isResponsiveProp,
  ResponsiveProp,
} from "@faire/web/slate/Theme/ResponsiveProp";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import React, { CSSProperties } from "react";

import { EAEAEA_PIXEL } from "@faire/retailer/consts/EAEAEA_PIXEL";

import { SDUIComponentRenderBehaviour } from "../types";

interface ImgStyles {
  objectFit?: CSSProperties["objectFit"];
  objectPosition?: CSSProperties["objectPosition"];
}

export const objectFitCoverImageStyles: ImgStyles = {
  objectFit: "cover",
  objectPosition: "center",
};

interface SDUIImageProps {
  responsiveImage: ResponsiveValues<IImage>;
  optimizeImageOptions: ResponsiveProp<IOptimizeImageOptions>;
  className?: string;
  renderBehaviour?: SDUIComponentRenderBehaviour;
  altText?: string;
  isProductImage?: boolean;
  imageStyles?: ImgStyles;
}

const SUPPORTED_DPR = [1, 2];

export const SDUIImage: React.FC<SDUIImageProps> = ({
  responsiveImage,
  optimizeImageOptions,
  className,
  renderBehaviour,
  altText,
  isProductImage,
  imageStyles,
}) => {
  const { imageUrlsMobile, imageUrlsTablet, imageUrlsDesktop } =
    getResponsiveImageUrls(
      responsiveImage,
      optimizeImageOptions,
      isProductImage
    );

  const smallestImageUrlAvailable =
    imageUrlsMobile?.[0] ?? imageUrlsTablet?.[0] ?? imageUrlsDesktop?.[0];

  return (
    <picture className={className}>
      {imageUrlsDesktop?.length ? (
        <source
          media={`(min-width: ${MIN_WIDTH_BREAKPOINTS.DESKTOP}px)`}
          srcSet={buildSrcSet(imageUrlsDesktop)}
        />
      ) : null}
      {imageUrlsTablet?.length ? (
        <source
          media={`(min-width: ${MIN_WIDTH_BREAKPOINTS.TABLET}px)`}
          srcSet={buildSrcSet(imageUrlsTablet)}
        />
      ) : null}
      {imageUrlsMobile?.length ? (
        <source
          media={`(min-width: ${MIN_WIDTH_BREAKPOINTS.MOBILE}px)`}
          srcSet={buildSrcSet(imageUrlsMobile)}
        />
      ) : null}
      <img
        style={{
          objectFit: imageStyles?.objectFit,
          objectPosition: imageStyles?.objectPosition,
        }}
        src={smallestImageUrlAvailable ?? EAEAEA_PIXEL}
        alt={altText ?? responsiveImage.mobileAndAbove?.alt_text}
        loading={renderBehaviour?.imagesLoadingStrategy}
        width="100%"
        height="100%"
      />
    </picture>
  );
};

const buildSrcSet = (imageUrls: string[]) => {
  return imageUrls
    .map((imageUrl, index) => {
      return `${imageUrl} ${SUPPORTED_DPR[index]}x`;
    })
    .join(", ");
};

const getOptimizedImageUrls = (
  image: IImage,
  optimizeImageOptions: IOptimizeImageOptions,
  isProductImage?: boolean
): string[] | undefined => {
  if (!image.url) {
    return undefined;
  }
  if (
    !isProductImage &&
    optimizeImageOptions.resizeMethod &&
    (!optimizeImageOptions.width || !optimizeImageOptions.height)
  ) {
    return undefined;
  }
  return SUPPORTED_DPR.map((dpr) => {
    return isProductImage
      ? optimizeProductImageUrl(
          { url: image.url },
          false,
          optimizeImageOptions.width ?? optimizeImageOptions.height,
          { ...optimizeImageOptions, dprOverride: dpr }
        )
      : optimizeImageUrl({ url: image.url }, false, {
          ...optimizeImageOptions,
          dprOverride: dpr,
        });
  }).filter(isNotUndefined);
};

export const getResponsiveImageUrls = (
  responsiveImage: ResponsiveValues<IImage>,
  optimizeImageOptions: ResponsiveProp<IOptimizeImageOptions>,
  isProductImage: boolean | undefined
): Partial<{
  imageUrlsMobile: string[];
  imageUrlsTablet: string[];
  imageUrlsDesktop: string[];
}> => {
  const imageOptionsMobile = isResponsiveProp(optimizeImageOptions)
    ? optimizeImageOptions.mobileAndAbove
    : optimizeImageOptions;
  const imageOptionsTablet = isResponsiveProp(optimizeImageOptions)
    ? optimizeImageOptions.tabletAndAbove
    : undefined;
  const imageOptionsDesktop = isResponsiveProp(optimizeImageOptions)
    ? optimizeImageOptions.desktopAndAbove
    : undefined;

  const imageMobile = responsiveImage.mobileAndAbove;
  const imageTablet = responsiveImage.tabletAndAbove;
  const imageDesktop = responsiveImage.desktopAndAbove;
  if (!imageMobile && !imageTablet && !imageDesktop) {
    return {};
  }

  const imageUrlsMobile = getOptimizedImageUrls(
    imageMobile,
    imageOptionsMobile,
    isProductImage
  );

  const imageUrlsTablet =
    imageOptionsTablet || imageTablet
      ? getOptimizedImageUrls(
          imageTablet ?? imageMobile,
          imageOptionsTablet ?? imageOptionsMobile,
          isProductImage
        )
      : undefined;

  const imageUrlsDesktop =
    imageOptionsDesktop || imageDesktop
      ? getOptimizedImageUrls(
          imageDesktop ?? imageTablet ?? imageMobile,
          imageOptionsDesktop ?? imageOptionsTablet ?? imageOptionsMobile,
          isProductImage
        )
      : undefined;

  return { imageUrlsMobile, imageUrlsTablet, imageUrlsDesktop };
};
